// Types for Merchandise Movement functionality (Entries and Exits)

export type MovementDirection = 'entry' | 'exit';

export interface MerchandiseMovement {
  id: string;
  movementNumber: string;
  direction: MovementDirection;
  ingredientId: string;
  ingredientName: string;
  locationId: string;
  locationName: string;
  quantity: number;
  unit: string;
  unitCost: number;
  totalCost: number;
  movementType: MerchandiseMovementType;
  movementTypeName?: string;
  reason: string;
  description?: string;
  status: MerchandiseMovementStatus;
  requiresApproval: boolean;
  requestedBy: string;
  requestedAt: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  cancelledBy?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  // Entry-specific fields
  supplierId?: string;
  supplierName?: string;
  purchaseOrderId?: string;
  batchNumber?: string;
  expirationDate?: string;
  receivedBy?: string;
  // Exit-specific fields
  issuedTo?: string;
  createdAt: string;
  updatedAt: string;
}

// Entry types
export type MerchandiseEntryType = 
  | 'purchase'        // Compra
  | 'return'          // Devolución de cliente
  | 'transfer_in'     // Transferencia entrante
  | 'adjustment_in'   // Ajuste positivo
  | 'donation'        // Donación
  | 'found'           // Producto encontrado
  | 'production'      // Producción interna
  | 'other_entry';    // Otro ingreso

// Exit types
export type MerchandiseExitType = 
  | 'waste'           // Merma
  | 'internal_use'    // Consumo interno
  | 'loss'           // Pérdida
  | 'damage'         // Daño
  | 'expired'        // Vencido
  | 'quality_issue'  // Problema de calidad
  | 'adjustment_out' // Ajuste negativo
  | 'sample'         // Muestra
  | 'transfer_out'   // Transferencia saliente
  | 'return_supplier'// Devolución a proveedor
  | 'other_exit';    // Otra salida

export type MerchandiseMovementType = MerchandiseEntryType | MerchandiseExitType;

export type MerchandiseMovementStatus =
  | 'pending_approval'  // Pendiente de aprobación
  | 'approved'         // Aprobado y aplicado al inventario
  | 'rejected'         // Rechazado
  | 'cancelled';       // Cancelado

export interface MerchandiseMovementFormData {
  direction: MovementDirection;
  ingredientId: string;
  locationId: string;
  quantity: number;
  movementType: MerchandiseMovementType;
  reason: string;
  description?: string;
  // Entry-specific
  supplierId?: string;
  purchaseOrderId?: string;
  batchNumber?: string;
  expirationDate?: string;
  // Exit-specific
  issuedTo?: string;
}

export interface MerchandiseMovementFilters {
  direction: MovementDirection | 'all';
  locationId: string;
  ingredientId: string;
  movementType: MerchandiseMovementType | 'all';
  status: MerchandiseMovementStatus | 'all';
  dateFrom: string;
  dateTo: string;
  requestedBy: string;
  supplierId?: string;
}

export interface MerchandiseMovementStats {
  // General stats
  total: number;
  pendingApproval: number;
  approved: number;
  rejected: number;
  totalValue: number;
  pendingValue: number;
  
  // Entry stats
  totalEntries: number;
  entriesValue: number;
  
  // Exit stats
  totalExits: number;
  exitsValue: number;
}

// Utility functions for entries
export const getEntryTypeLabel = (type: MerchandiseEntryType): string => {
  const labels = {
    purchase: 'Compra',
    return: 'Devolución de Cliente',
    transfer_in: 'Transferencia Entrante',
    adjustment_in: 'Ajuste Positivo',
    donation: 'Donación',
    found: 'Producto Encontrado',
    production: 'Producción Interna',
    other_entry: 'Otro Ingreso'
  };
  return labels[type];
};

export const getEntryTypeColor = (type: MerchandiseEntryType): string => {
  const colors = {
    purchase: 'bg-green-100 text-green-800',
    return: 'bg-blue-100 text-blue-800',
    transfer_in: 'bg-teal-100 text-teal-800',
    adjustment_in: 'bg-emerald-100 text-emerald-800',
    donation: 'bg-purple-100 text-purple-800',
    found: 'bg-cyan-100 text-cyan-800',
    production: 'bg-indigo-100 text-indigo-800',
    other_entry: 'bg-slate-100 text-slate-800'
  };
  return colors[type];
};

// Utility functions for exits
export const getExitTypeLabel = (type: MerchandiseExitType): string => {
  const labels = {
    waste: 'Merma',
    internal_use: 'Consumo Interno',
    loss: 'Pérdida',
    damage: 'Daño',
    expired: 'Vencido',
    quality_issue: 'Problema de Calidad',
    adjustment_out: 'Ajuste Negativo',
    sample: 'Muestra',
    transfer_out: 'Transferencia Saliente',
    return_supplier: 'Devolución a Proveedor',
    other_exit: 'Otra Salida'
  };
  return labels[type];
};

export const getExitTypeColor = (type: MerchandiseExitType): string => {
  const colors = {
    waste: 'bg-red-100 text-red-800',
    internal_use: 'bg-blue-100 text-blue-800',
    loss: 'bg-orange-100 text-orange-800',
    damage: 'bg-yellow-100 text-yellow-800',
    expired: 'bg-purple-100 text-purple-800',
    quality_issue: 'bg-pink-100 text-pink-800',
    adjustment_out: 'bg-gray-100 text-gray-800',
    sample: 'bg-green-100 text-green-800',
    transfer_out: 'bg-amber-100 text-amber-800',
    return_supplier: 'bg-violet-100 text-violet-800',
    other_exit: 'bg-slate-100 text-slate-800'
  };
  return colors[type];
};

// Combined utility functions
export const getMovementTypeLabel = (type: MerchandiseMovementType): string => {
  if (type in getEntryTypeLabel) {
    return getEntryTypeLabel(type as MerchandiseEntryType);
  }
  return getExitTypeLabel(type as MerchandiseExitType);
};

export const getMovementTypeColor = (type: MerchandiseMovementType): string => {
  if (type in getEntryTypeColor) {
    return getEntryTypeColor(type as MerchandiseEntryType);
  }
  return getExitTypeColor(type as MerchandiseExitType);
};

export const getStatusLabel = (status: MerchandiseMovementStatus): string => {
  const labels = {
    pending_approval: 'Pendiente de Aprobación',
    approved: 'Aprobado',
    rejected: 'Rechazado',
    cancelled: 'Cancelado'
  };
  return labels[status];
};

export const getStatusColor = (status: MerchandiseMovementStatus): string => {
  const colors = {
    pending_approval: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800'
  };
  return colors[status];
};

export const getDirectionLabel = (direction: MovementDirection): string => {
  return direction === 'entry' ? 'Ingreso' : 'Salida';
};

export const getDirectionColor = (direction: MovementDirection): string => {
  return direction === 'entry' 
    ? 'bg-green-100 text-green-800' 
    : 'bg-red-100 text-red-800';
};

// Configuration for movement types that require approval
export const ENTRY_TYPES_REQUIRING_APPROVAL: MerchandiseEntryType[] = [
  'adjustment_in',
  'found',
  'donation'
];

export const EXIT_TYPES_REQUIRING_APPROVAL: MerchandiseExitType[] = [
  'loss',
  'damage',
  'adjustment_out'
];

// Configuration for approval thresholds
export const APPROVAL_THRESHOLD_AMOUNT = 100; // Amount in currency that requires approval
export const APPROVAL_THRESHOLD_PERCENTAGE = 5; // Percentage of current stock that requires approval

export const requiresApproval = (
  direction: MovementDirection,
  movementType: MerchandiseMovementType, 
  quantity: number, 
  unitCost: number,
  currentStock: number
): boolean => {
  // Check if movement type always requires approval
  if (direction === 'entry' && ENTRY_TYPES_REQUIRING_APPROVAL.includes(movementType as MerchandiseEntryType)) {
    return true;
  }
  
  if (direction === 'exit' && EXIT_TYPES_REQUIRING_APPROVAL.includes(movementType as MerchandiseExitType)) {
    return true;
  }

  const totalValue = quantity * unitCost;
  const stockPercentage = currentStock > 0 ? (quantity / currentStock) * 100 : 0;

  // Check if amount or percentage exceeds thresholds
  return totalValue >= APPROVAL_THRESHOLD_AMOUNT || stockPercentage >= APPROVAL_THRESHOLD_PERCENTAGE;
};

// Legacy type aliases for backward compatibility
export type MerchandiseExit = MerchandiseMovement;
export type MerchandiseExitFormData = MerchandiseMovementFormData;
export type MerchandiseExitFilters = MerchandiseMovementFilters;
export type MerchandiseExitStats = MerchandiseMovementStats;